/**
 * Settings ▸ SDMS ▸ Template Formula (legacy SpreadSheetBuilder_TemplateInfo_FormulaSetup_ListView /
 * _DetailView): which SDMS formulas run on a template, and in what order.
 *
 * LIST — the templates that have formulas, with the legacy bar Edit · Refresh · Add Formulas ·
 * Export to and a tick column. DETAIL (Edit, a row double-click, or Add Formulas → pick a template)
 * — the template's header, read-only, over the "Template Formulas" grid with Delete · Add Formulas ·
 * Export to; Save · Save and Close · Cancel top and bottom.
 *
 * It is a second face on the SAME data and API as Settings ▸ Formula Setup ▸ SDMS Formula Setup
 * (`/api/v1/sdms-formula`, dbo.SpreadSheetBuilder_TemplateFormula) — no second writer was built
 * (standing rule 8). That API stores a template's formulas as a set and audits the save as
 * "SDMS Formula Setup", so this page uses that form name too.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppDialog } from '../../components/AppDialog';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import '../modules/module.css';
import { FormTitle } from '../../components/FormTitle';
import { useConfirm, useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo, effectiveCombo } from '../../components/hotkeys';
import { useNotify } from '../../components/notifications';
import { useNavPermission } from '../../hooks/useNavPermission';
import { guardedExit, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { ModuleCrudDialog } from '../modules/ModuleCrudDialog';
import { MODULE_CRUD, type ModuleCrudConfig } from '../modules/moduleCrud';
import '../modules/SettingsDetailPage.css';
import './templateFormula.css';

const PAGE_ID = 'set-sdms-template-formula';
/** dbo.AuditData.FormName the shared API writes — see the file header. */
const FORM_NAME = 'SDMS Formula Setup';
const API = '/api/v1/sdms-formula';

interface MapRow {
  test: string | null; method: string | null; templateID: number; templateName: string | null;
  templateType: string | null; formulaCount: number;
}
interface LibRow {
  formulaUqId: number; formulaID: string | null; formulaName: string; series: number;
  /** 0 = Fixed (a built-in API function), 1 = Script. */
  kind?: number | null; apiFunction?: string | null; targetField?: string | null; caption?: string | null;
}
/** One row of the "Select Target Templates" popup — a template with its linked tests joined. */
interface TemplateOpt {
  templateID: number; templateName: string | null; test: string | null;
  method?: string | null; matrix?: string | null; templateType?: string | null;
}
/** A row of the SDMS Template Builder list: one per template + linked test. */
interface TemplateTestRow { oid?: string; test?: string | null; method?: string | null; matrix?: string | null; templateType?: string | null }
/** One row of the Template Formulas grid. */
interface FormulaRow { formulaUqId: number; formulaID: string; formulaName: string; series: number; executionOrder: number; isActive: boolean }
interface Header { templateID: number; templateName: string; test: string; method: string; matrix: string }

const LIST_COLS: AppColumn<MapRow>[] = [
  { field: 'test', header: 'Test', width: 200 },
  { field: 'method', header: 'Method', width: 180 },
  { field: 'templateID', header: 'TemplateID', type: 'number', width: 120 },
  { field: 'templateName', header: 'TemplateName', width: 240 },
  { field: 'templateType', header: 'TemplateType', width: 160 },
  { field: 'formulaCount', header: 'No.ofFormulas', type: 'number', width: 130 },
];
// Legacy "Select Target Templates" columns, in the legacy order.
const TEMPLATE_PICK_COLS: AppColumn<TemplateOpt>[] = [
  { field: 'test', header: 'Test', width: 200, wrap: true },
  { field: 'method', header: 'Method', width: 150 },
  { field: 'matrix', header: 'Matrix', width: 115 },
  { field: 'templateID', header: 'TemplateID', type: 'number', width: 150 },
  { field: 'templateType', header: 'TemplateType', width: 150 },
  { field: 'templateName', header: 'TemplateName', width: 190 },
];
/** Distinct non-empty values, joined the way the legacy popup shows a template's tests. */
const joinDistinct = (values: (string | null | undefined)[]) =>
  [...new Set(values.map(v => (v ?? '').trim()).filter(Boolean))].join(', ');
const kindName = (r: LibRow) => (r.kind === 1 ? 'Script' : r.kind === 0 ? 'Fixed' : '');
// Legacy formula picker columns, in the legacy order. The library lists active formulas only.
const FORMULA_PICK_COLS: AppColumn<LibRow>[] = [
  { field: 'formulaID', header: 'FormulaID', width: 150 },
  { field: 'formulaName', header: 'FormulaName', width: 165, wrap: true },
  { field: 'kind', header: 'Kind', width: 110, value: kindName, text: kindName },
  { field: 'apiFunction', header: 'ApiFunction', width: 150, wrap: true },
  { field: 'targetField', header: 'TargetField', width: 140 },
  { field: 'series', header: 'Series', type: 'number', width: 120 },
  { field: 'caption', header: 'Caption', width: 120, wrap: true },
  { field: 'isActive' as keyof LibRow & string, header: 'IsActive', width: 135, align: 'center',
    value: () => 'Yes', text: () => 'Yes',
    render: r => <input type="checkbox" checked disabled readOnly aria-label={`IsActive — ${r.formulaName}`} /> },
];

/** The shared Formula form (Settings ▸ SDMS ▸ SDMS Formulas) — its fields, API and audit. */
// Shown here WITHOUT the helper text under each field (the legacy popup has none, and it made
// this popup long) and with Kind as radio buttons, as legacy. A copy, so the SDMS Formulas
// form itself is unchanged.
const FORMULA_FORM: ModuleCrudConfig = {
  ...MODULE_CRUD.formulalib!,
  fields: MODULE_CRUD.formulalib!.fields.map(f => ({ ...f, hint: undefined, radioInDialog: f.key === 'kind' ? true : undefined })),
};
/** What the legacy New formula form opens with: Fixed, series 0, active. */
const NEW_FORMULA = { kind: '0', series: '0', isActive: true };

const sameRows = (a: FormulaRow[], b: FormulaRow[]) =>
  a.length === b.length && a.every((r, i) => r.formulaUqId === b[i]!.formulaUqId && r.executionOrder === b[i]!.executionOrder);

export function TemplateFormulaPage({ onHome }: { onHome: () => void }) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const runSave = useSaveFlow();
  const perm = useNavPermission('SDMS');

  // ── list ────────────────────────────────────────────────────────────────
  const [maps, setMaps] = useState<MapRow[]>([]);
  const [lib, setLib] = useState<LibRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // Page-level maximize (ModuleListView pattern): hides the breadcrumb, title and toolbar, keeps the search row
  // with Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMax]);
  const [ticked, setTicked] = useState<Set<string | number>>(new Set());
  const [templates, setTemplates] = useState<TemplateOpt[]>([]);
  const [pickTemplate, setPickTemplate] = useState(false);
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateTicks, setTemplateTicks] = useState<Set<string | number>>(new Set());

  // ── detail ──────────────────────────────────────────────────────────────
  const [header, setHeader] = useState<Header | null>(null);
  // View → Edit (owner request 2026-10-13): a click on a row opens the detail READ-ONLY with an Edit
  // button; the pencil, the toolbar Edit and Add Formulas open it editable. While viewing, the page
  // behaves exactly as it does without Write permission — every guard below keys off `canWrite`.
  const [mode, setMode] = useState<'view' | 'edit'>('edit');
  const viewing = mode === 'view' && header != null;
  const canWrite = perm.canWrite && !viewing;
  const [rows, setRows] = useState<FormulaRow[]>([]);
  const [saved, setSaved] = useState<FormulaRow[]>([]);
  const [rowTicks, setRowTicks] = useState<Set<string | number>>(new Set());
  const [pickFormulas, setPickFormulas] = useState(false);
  /** The formula form over the picker: `id` null = New, else the formula being edited. */
  const [formulaForm, setFormulaForm] = useState<{ id: string | null } | null>(null);
  const [formulaTicks, setFormulaTicks] = useState<Set<string | number>>(new Set());
  const [formulaSearch, setFormulaSearch] = useState('');
  const [saving, setSaving] = useState(false);
  /** The Template Name box — the one editable field of the header (legacy parity). */
  const [name, setName] = useState('');

  const nameChanged = header != null && name.trim() !== header.templateName;
  const dirty = header != null && (!sameRows(rows, saved) || nameChanged);
  useUnsavedGuard(dirty);

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const [m, l] = await Promise.all([
        api.get<{ data?: MapRow[] }>(`${API}/mappings`),
        api.get<{ data?: LibRow[] }>(`${API}/library`),
      ]);
      setMaps(m.data?.data ?? []);
      setLib(l.data?.data ?? []);
      setTicked(new Set());
    } catch {
      notify.error('The template formulas could not be loaded. Check that the server is running, then press Refresh.');
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return maps;
    return maps.filter(m => [m.test, m.method, m.templateName, m.templateType, String(m.templateID)]
      .some(v => (v ?? '').toLowerCase().includes(q)));
  }, [maps, search]);

  // Each template's linked tests (one row per template + test), read once. The formula API does
  // not return the Matrix, so the detail header and the template popup take it from here.
  const linkedRef = useRef<Map<number, TemplateTestRow[]> | null>(null);
  const linkedTests = async () => {
    if (linkedRef.current) return linkedRef.current;
    const res = await api.get<{ data?: TemplateTestRow[] }>('/api/v1/modules/set-sdms-template-builder', { params: { page: 1, pageSize: 2000 } });
    const byTemplate = new Map<number, TemplateTestRow[]>();
    for (const row of res.data?.data ?? []) {
      const id = Number.parseInt(String(row.oid ?? '').split('|')[0] ?? '', 10);
      if (Number.isFinite(id)) byTemplate.set(id, [...(byTemplate.get(id) ?? []), row]);
    }
    linkedRef.current = byTemplate;
    return byTemplate;
  };

  // ── open / close the detail ─────────────────────────────────────────────
  const open = async (opened: Omit<Header, 'matrix'> & { matrix?: string }) => {
    try {
      const [d, linked] = await Promise.all([
        api.get<{ data?: Partial<FormulaRow>[] }>(`${API}/mappings/${opened.templateID}`),
        linkedTests(),
      ]);
      const h: Header = { ...opened, matrix: opened.matrix ?? joinDistinct((linked.get(opened.templateID) ?? []).map(x => x.matrix)) };
      const list: FormulaRow[] = (d.data?.data ?? []).map(r => ({
        formulaUqId: Number(r.formulaUqId), formulaID: r.formulaID ?? '', formulaName: r.formulaName ?? '',
        series: Number(r.series ?? 0), executionOrder: Number(r.executionOrder ?? 0), isActive: r.isActive !== false,
      }));
      setRows(list); setSaved(list); setRowTicks(new Set()); setName(h.templateName); setHeader(h);
    } catch {
      notify.error('The template\'s formulas could not be loaded.');
    }
  };
  const openAs = (m: MapRow, as: 'view' | 'edit') => {
    setMode(as);
    void open({ templateID: m.templateID, templateName: m.templateName ?? '', test: m.test ?? '', method: m.method ?? '' });
  };
  const openRow = (m: MapRow) => openAs(m, 'edit');
  const viewRow = (m: MapRow) => openAs(m, 'view');
  // The list's columns, led by the legacy edit (pencil) column, which opens the row's detail.
  // The export keeps LIST_COLS, so the pencil is not a column in the exported file.
  const listCols: AppColumn<MapRow>[] = [
    { field: 'edit' as keyof MapRow & string, header: '', width: 40, align: 'center', sortable: false, filterable: false,
      value: () => '', text: () => '',
      render: m => (
        <button type="button" className="tf-row-edit" title="Edit" aria-label={`Edit ${m.templateName ?? m.templateID}`}
          onClick={e => { e.stopPropagation(); openRow(m); }}>
          <i className="ti ti-pencil" aria-hidden="true" />
        </button>
      ) },
    ...LIST_COLS,
  ];
  const editTicked = () => {
    const picked = maps.filter(m => ticked.has(m.templateID));
    if (picked.length !== 1) { void alert({ title: 'Template Formula', message: 'Select one template to edit.', tone: 'warning' }); return; }
    openRow(picked[0]!);
  };
  const close = () => { setHeader(null); setRows([]); setSaved([]); void load(); };
  const cancel = () => { void guardedExit(close); };

  // ── Add Formulas (list): choose the template, then its detail opens ─────
  const startAdd = async () => {
    try {
      if (templates.length === 0) {
        // The formula API names the templates; the template list supplies each one's linked
        // tests (one row per template + test), joined here as the legacy popup shows them.
        const [r, byTemplate] = await Promise.all([
          api.get<{ data?: TemplateOpt[] }>(`${API}/templates`),
          linkedTests(),
        ]);
        setTemplates((r.data?.data ?? []).map(t => {
          const tests = byTemplate.get(t.templateID) ?? [];
          return {
            ...t,
            test: joinDistinct(tests.map(x => x.test)) || t.test,
            method: joinDistinct(tests.map(x => x.method)),
            matrix: joinDistinct(tests.map(x => x.matrix)),
            templateType: joinDistinct(tests.map(x => x.templateType)),
          };
        }).sort((a, b) => a.templateID - b.templateID));
      }
      setTemplateSearch('');
      setTemplateTicks(new Set());
      setPickTemplate(true);
    } catch {
      notify.error('The templates could not be loaded.');
    }
  };
  const templateChoices = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    return templates.filter(t => !q || [t.templateID, t.templateName, t.test, t.method, t.matrix, t.templateType]
      .some(v => String(v ?? '').toLowerCase().includes(q)));
  }, [templates, templateSearch]);
  const chooseTemplate = (t: TemplateOpt) => {
    setMode('edit');
    setPickTemplate(false);
    const m = maps.find(x => x.templateID === t.templateID);
    void open({ templateID: t.templateID, templateName: t.templateName ?? '', test: m?.test ?? t.test ?? '', method: m?.method ?? t.method ?? '' });
  };
  // The popup's columns, led by the legacy edit (pencil) column, which opens that template.
  const templatePickCols: AppColumn<TemplateOpt>[] = [
    { field: 'edit' as keyof TemplateOpt & string, header: '', width: 44, align: 'center', sortable: false, filterable: false,
      value: () => '', text: () => '',
      render: t => (
        <button type="button" className="tf-row-edit" title="Edit" aria-label={`Edit ${t.templateName ?? t.templateID}`}
          onClick={e => { e.stopPropagation(); chooseTemplate(t); }}>
          <i className="ti ti-pencil" aria-hidden="true" />
        </button>
      ) },
    ...TEMPLATE_PICK_COLS,
  ];
  const openTickedTemplate = () => {
    const picked = templates.filter(t => templateTicks.has(t.templateID));
    if (picked.length !== 1) { void alert({ title: 'Select Target Templates', message: 'Select one template to open.', tone: 'warning' }); return; }
    chooseTemplate(picked[0]!);
  };

  // ── Template Formulas grid ──────────────────────────────────────────────
  const formulaChoices = useMemo(() => {
    const used = new Set(rows.map(r => r.formulaUqId));
    const q = formulaSearch.trim().toLowerCase();
    return lib.filter(f => !used.has(f.formulaUqId)
      && (!q || [f.formulaID, f.formulaName, kindName(f), f.apiFunction, f.targetField, f.series, f.caption]
        .some(v => String(v ?? '').toLowerCase().includes(q))))
      .sort((a, b) => a.formulaName.localeCompare(b.formulaName));
  }, [lib, rows, formulaSearch]);
  const addFormulas = () => {
    const picks = lib.filter(f => formulaTicks.has(f.formulaUqId));
    if (picks.length === 0) return;
    setRows(r => [...r, ...picks.map(f => ({
      formulaUqId: f.formulaUqId, formulaID: f.formulaID ?? '', formulaName: f.formulaName,
      series: f.series, executionOrder: f.series, isActive: true,
    }))]);
    setPickFormulas(false);
  };
  const formulaSaved = async () => {
    setFormulaForm(null);
    try {
      const l = await api.get<{ data?: LibRow[] }>(`${API}/library`);
      const fresh = l.data?.data ?? [];
      setLib(fresh);
      const byId = new Map(fresh.map(f => [f.formulaUqId, f]));
      const relabel = (list: FormulaRow[]) => list.map(r => {
        const f = byId.get(r.formulaUqId);
        return f ? { ...r, formulaID: f.formulaID ?? '', formulaName: f.formulaName, series: f.series } : r;
      });
      setRows(relabel); setSaved(relabel);
    } catch {
      notify.error('The formulas could not be reloaded. Close and reopen this popup.');
    }
  };
  // The picker's columns, led by the legacy edit (pencil) column.
  const formulaPickCols: AppColumn<LibRow>[] = [
    { field: 'edit' as keyof LibRow & string, header: '', width: 44, align: 'center', sortable: false, filterable: false,
      value: () => '', text: () => '',
      render: r => (
        <button type="button" className="tf-row-edit" title="Edit" aria-label={`Edit ${r.formulaName}`}
          onClick={e => { e.stopPropagation(); setFormulaForm({ id: String(r.formulaUqId) }); }}>
          <i className="ti ti-pencil" aria-hidden="true" />
        </button>
      ) },
    ...FORMULA_PICK_COLS,
  ];
  const removeTicked = () => { setRows(r => r.filter(x => !rowTicks.has(x.formulaUqId))); setRowTicks(new Set()); };

  const formulaCols: AppColumn<FormulaRow>[] = [
    { field: 'formulaID', header: 'FormulaID', width: 160 },
    { field: 'series', header: 'ApiSeries', type: 'number', width: 130 },
    { field: 'formulaName', header: 'Formula', width: 320 },
    { field: 'executionOrder', header: 'ExecutionOrder', type: 'number', width: 150, edit: canWrite ? { type: 'number' } : undefined },
    { field: 'isActive', header: 'IsActive', width: 100, align: 'center',
      value: r => (r.isActive ? 'Yes' : 'No'), text: r => (r.isActive ? 'Yes' : 'No'),
      render: r => <input type="checkbox" checked={r.isActive} disabled readOnly aria-label={`IsActive — ${r.formulaName}`} /> },
  ];

  // ── save ────────────────────────────────────────────────────────────────
  const save = (thenClose: boolean) => {
    if (!header || !canWrite || saving) return;
    const newName = name.trim();
    if (!newName) { void alert({ title: 'Template Formula', message: 'Template Name must not be empty.', tone: 'warning' }); return; }
    const formulasChanged = !sameRows(rows, saved);
    // A rename on its own may be saved for a template that has no formulas yet.
    if (rows.length === 0 && (formulasChanged || !nameChanged)) {
      void alert({ title: 'Template Formula', message: 'Add at least one formula.', tone: 'warning' }); return;
    }
    if (rows.some(r => !(r.executionOrder > 0))) { void alert({ title: 'Template Formula', message: 'ExecutionOrder must be 1 or more.', tone: 'warning' }); return; }
    const isNew = !maps.some(m => m.templateID === header.templateID);
    void runSave({
      formName: FORM_NAME,
      auditAction: isNew ? 'create' : 'edit',
      save: async () => {
        setSaving(true);
        try {
          // The name first: it is the one that can be refused (another template has it), and a
          // refusal must leave the formulas unsaved too rather than half the screen stored.
          if (nameChanged) await api.put(`${API}/templates/${header.templateID}/name`, { name: newName });
          if (rows.length > 0 && (formulasChanged || !nameChanged)) {
            await api.put(`${API}/mappings/${header.templateID}`, {
              templateID: header.templateID,
              formulas: [...rows].sort((a, b) => a.executionOrder - b.executionOrder)
                .map(r => ({ formulaUqId: r.formulaUqId, executionOrder: r.executionOrder })),
            });
          }
        } finally { setSaving(false); }
      },
      onSaved: () => { if (thenClose) close(); else void open({ ...header, templateName: newName }); },
    });
  };

  useHotkeys(header == null ? [] : viewing ? [
    { combo: 'alt+e', label: 'Edit', group: 'Detail', order: 1, run: () => setMode('edit'), disabled: !perm.canWrite },
    { combo: 'f2', label: 'Edit', group: 'Detail', order: 2, run: () => setMode('edit'), disabled: !perm.canWrite },
    { combo: 'esc', label: 'Close', group: 'Detail', order: 3, run: close, allowInInput: false },
  ] : [
    { combo: 'mod+s', label: 'Save', group: 'Detail', order: 1, run: () => save(false), disabled: !canWrite || saving },
    { combo: 'mod+enter', label: 'Save and Close', group: 'Detail', order: 2, run: () => save(true), disabled: !canWrite || saving },
    { combo: 'esc', label: 'Cancel', group: 'Detail', order: 3, run: cancel, allowInInput: false },
  ]);

  // The shared detail page's action bar (Save · Save and Close · | · Cancel), top and bottom.
  // No Save and New: a template's formulas are one set, there is no blank record to start.
  const actionBar = (bottom?: boolean) => viewing ? (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button type="button" className="sd-abtn primary" disabled={!perm.canWrite} onClick={() => setMode('edit')}
        title={perm.canWrite ? `Edit (${formatCombo(effectiveCombo('alt+e'))})` : 'You do not have Write permission for SDMS'}><span className="tb-i tb-i-blue">✎</span> Edit</button>
      <button type="button" className="sd-abtn cancel" onClick={close}
        title={`Close (${formatCombo(effectiveCombo('esc'))})`}><span className="tb-i">↩︎</span> Close</button>
    </div>
  ) : (
    <div className={`sd-actionbar${bottom ? ' bottom' : ''}`}>
      <button type="button" className="sd-abtn primary" disabled={!canWrite || saving} onClick={() => save(false)}
        title={`Save (${formatCombo(effectiveCombo('mod+s'))})`}><span className="tb-i">💾</span> Save</button>
      <button type="button" className="sd-abtn" disabled={!canWrite || saving} onClick={() => save(true)}
        title={`Save and Close (${formatCombo(effectiveCombo('mod+enter'))})`}><span className="tb-i">💾</span> Save and Close</button>
      <span className="sd-abtn-sep" aria-hidden="true" />
      <button type="button" className="sd-abtn cancel" disabled={saving} onClick={cancel}
        title={`Cancel (${formatCombo(effectiveCombo('esc'))})`}><span className="tb-i">↩︎</span> Cancel</button>
    </div>
  );

  // Previous / next record walk the list as it is filtered.
  const at = header ? shown.findIndex(m => m.templateID === header.templateID) : -1;
  const prevRow = at > 0 ? shown[at - 1] : undefined;
  const nextRow = at >= 0 && at < shown.length - 1 ? shown[at + 1] : undefined;
  const step = (m?: MapRow) => { if (m) void guardedExit(() => (viewing ? viewRow : openRow)(m)); };

  const crumbs = (last: string, backToList?: () => void) => (
    <div className="dp-breadcrumb">
      <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
      <i className="ti ti-chevron-right" /><span>Settings</span>
      <i className="ti ti-chevron-right" /><span>SDMS</span>
      <i className="ti ti-chevron-right" />
      {backToList ? <a onClick={backToList} style={{ cursor: 'pointer' }}>Template Formula</a> : <span className="dp-bc-active">Template Formula</span>}
      {backToList && <><i className="ti ti-chevron-right" /><span className="dp-bc-active">{last}</span></>}
    </div>
  );

  // ── DETAIL ──────────────────────────────────────────────────────────────
  if (header) {
    const recordName = header.templateName || `Template ${header.templateID}`;
    // Read-only header values. Template Name is the one editable box and is rendered apart.
    const leftFields: [string, string | number][] = [['Matrix', header.matrix], ['Test', header.test], ['Method', header.method]];
    const readOnlyRow = ([label, value]: [string, string | number]) => (
      <div className="sd-row" key={label}>
        <label className="sd-label">{label}</label>
        <input className="sd-input readonly" readOnly tabIndex={-1} value={value} aria-label={label} />
      </div>
    );
    return (
      <div className="bl-page mlv-host tf-detail">
        <div className="bl-grid-area">
          <div className="sd-wrap">
            <div className="sd-head">
              <div>
                <h2>Template Formula – {recordName}</h2>
                <div className="sd-bc">
                  <a onClick={() => void guardedExit(onHome)}>Home</a>
                  <i className="ti ti-chevron-right" /><span>Settings</span>
                  <i className="ti ti-chevron-right" /><span>SDMS</span>
                  <i className="ti ti-chevron-right" /><a onClick={cancel}>Template Formula</a>
                  <i className="ti ti-chevron-right" /><span>{recordName}</span>
                </div>
              </div>
              <div className="sd-navarrows">
                <button type="button" className="sd-na" title="Previous record" disabled={!prevRow || saving} onClick={() => step(prevRow)}>‹</button>
                <button type="button" className="sd-na next" title="Next record" disabled={!nextRow || saving} onClick={() => step(nextRow)}>›</button>
              </div>
            </div>

            {actionBar()}

            <div className="sd-body">
              <h3 className="sd-section-h">General Information</h3>
              <div className="sd-grid sd-grid--colmajor sd-grid--side sd-grid--square" style={{ '--sd-rows': '3' } as CSSProperties}>
                {leftFields.map(readOnlyRow)}
                <div className="sd-row">
                  <label className="sd-label" htmlFor="tf-template-name">Template Name<span className="req-star"> *</span></label>
                  <input id="tf-template-name" className={`sd-input${canWrite ? '' : ' readonly'}`} readOnly={!canWrite}
                    maxLength={200} value={name} onChange={e => setName(e.target.value)} />
                </div>
                {readOnlyRow(['Template ID', header.templateID])}
                {readOnlyRow(['No. of Formulas', rows.length])}
              </div>

              <h3 className="sd-section-h sd-subsection-h">Template Formulas</h3>
              <AppToolbar
                noHotkeys
                crudFirst
                showMeta={false}
                onDelete={canWrite ? removeTicked : undefined}
                deleteDisabled={rowTicks.size === 0}
                left={canWrite ? (
                  <button type="button" className="ca-btn" onClick={() => { setFormulaTicks(new Set()); setFormulaSearch(''); setPickFormulas(true); }}>
                    <span className="tb-i tb-i-green">＋</span> Add Formulas</button>
                ) : undefined}
                exportData={{ filename: `Template Formulas - ${header.templateName}`, ...gridExportMatrix(rows, formulaCols) }}
              />
              <AppDataGrid<FormulaRow>
                rows={rows} columns={formulaCols} getRowId={r => r.formulaUqId}
                selectable={canWrite} selectedIds={rowTicks} onSelectionChange={setRowTicks}
                onCellCommit={(row, _col, v) => setRows(list => list.map(x => (x.formulaUqId === row.formulaUqId
                  ? { ...x, executionOrder: Math.max(0, Number.parseInt(v, 10) || 0) } : x)))}
                initialSort={{ key: 'executionOrder', dir: 'asc' }}
                defaultPageSize={20} maximizable exportFilename={`Template Formulas - ${header.templateName}`}
                emptyText="No data to display" />
            </div>

            {actionBar(true)}
          </div>
        </div>

        <AppDialog open={pickFormulas} title="Add Formulas" icon="ti-math-function" width={1180} onClose={() => setPickFormulas(false)}
          footer={<>
            <button type="button" className="ca-btn ca-btn-solid" disabled={formulaTicks.size === 0} onClick={addFormulas}>Add</button>
            <button type="button" className="ca-btn" onClick={() => setPickFormulas(false)}>Cancel</button>
          </>}>
          <div className="tf-pick-bar tf-pick-bar--new" data-unsaved-ignore>
            <button type="button" className="ca-btn" onClick={() => setFormulaForm({ id: null })}>
              <span className="tb-i tb-i-green">＋</span> New</button>
            <div className="list-search"><span className="tb-i">🔍</span>
              <input type="text" placeholder="Search…" aria-label="Search" value={formulaSearch} onChange={e => setFormulaSearch(e.target.value)} /></div>
          </div>
          <AppDataGrid<LibRow> rows={formulaChoices} columns={formulaPickCols} getRowId={r => r.formulaUqId}
            selectable selectedIds={formulaTicks} onSelectionChange={setFormulaTicks}
            defaultPageSize={20} fixedLayout hideHeaderTools emptyText="No data to display" />
        </AppDialog>

        {/* New / Edit formula — the shared Formula form, shown above the picker. */}
        {formulaForm && (
          <div className="tf-formula-form" data-unsaved-ignore>
            <ModuleCrudDialog config={FORMULA_FORM} id={formulaForm.id} mode="edit"
              contextValues={formulaForm.id == null ? NEW_FORMULA : undefined}
              onClose={() => setFormulaForm(null)} onSaved={() => void formulaSaved()} />
          </div>
        )}
      </div>
    );
  }

  // ── LIST ────────────────────────────────────────────────────────────────
  return (
    <div className={`bl-page mlv-host${gridMax ? ' page-fullgrid' : ''}`}>
      <div className="bl-grid-area">
        {!gridMax && (<>
        {crumbs('')}
        <FormTitle pageId={PAGE_ID} loadMs={loadMs} loading={loading} auditFormName={FORM_NAME} />

        {/* Legacy bar: Edit · Refresh · Add Formulas · Export to. Search sits on its own row below (2026-10-14). */}
        <div className="tf-list-bar">
        <AppToolbar
          editFirst
          onEdit={editTicked}
          editDisabled={ticked.size === 0}
          onRefresh={() => void load()}
          refreshing={loading}
          beforeExport={canWrite ? (
            <button type="button" className="ca-btn" onClick={() => void startAdd()}>
              <span className="tb-i tb-i-green">＋</span> Add Formulas</button>
          ) : undefined}
          exportData={{ filename: 'Template Formula', ...gridExportMatrix(shown, LIST_COLS) }}
        />
        </div>
        </>)}

        {/* Search + Export + Maximize on ONE row above the grid (ModuleListView layout, owner request 2026-10-14) —
            stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input type="text" placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="Template Formula" {...gridExportMatrix(shown, LIST_COLS)} />
            <button type="button" className={`dv-gridact${gridMax ? ' dv-on' : ''}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? 'Restore' : 'Maximize'}>
              <i className={`ti ti-arrows-${gridMax ? 'minimize' : 'maximize'}`} />
            </button>
          </div>
        </div>

        <AppDataGrid<MapRow>
          rows={shown} columns={listCols} getRowId={r => r.templateID} loading={loading}
          selectable selectedIds={ticked} onSelectionChange={setTicked}
          onRowClick={viewRow}
          defaultPageSize={20} hideHeaderTools emptyText="No data to display" />
      </div>

      <AppDialog open={pickTemplate} title="Select Target Templates" icon="ti-layout-grid" width={1100} onClose={() => setPickTemplate(false)}
        footer={<>
          <button type="button" className="ca-btn ca-btn-solid" disabled={templateTicks.size === 0} onClick={openTickedTemplate}>Open</button>
          <button type="button" className="ca-btn" onClick={() => setPickTemplate(false)}>Cancel</button>
        </>}>
        <div className="tf-pick-bar" data-unsaved-ignore>
          <div className="list-search"><span className="tb-i">🔍</span>
            <input type="text" placeholder="Search…" aria-label="Search" value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} /></div>
        </div>
        <AppDataGrid<TemplateOpt> rows={templateChoices} columns={templatePickCols} getRowId={r => r.templateID}
          selectable selectedIds={templateTicks} onSelectionChange={setTemplateTicks}
          onRowDoubleClick={chooseTemplate} defaultPageSize={20} fixedLayout hideHeaderTools emptyText="No data to display" />
      </AppDialog>
    </div>
  );
}
